/**
 * One screen of a non-trucking application: the fields the server sends in a FORM chat component, the answers
 * held while the customer fills them in, and the checks and payload that mirror the server's
 * (cailabs/insure/services/insurance/actions/contractor_form.py).
 *
 * Object keys arrive camel-cased (toCamelCase), so answer maps travel as [key, value] pairs.
 */
import { format, isValid, parse } from 'date-fns'

import { US_STATES } from '@/lib/usStates'

export enum ScreenFieldKind {
  TEXT = 'TEXT',
  LONG_TEXT = 'LONG_TEXT',
  NUMBER = 'NUMBER',
  MONEY = 'MONEY',
  YEAR = 'YEAR',
  DATE = 'DATE',
  MONTH = 'MONTH',
  CHOICE = 'CHOICE',
  SELECT = 'SELECT',
  MULTI = 'MULTI',
  CARDS = 'CARDS',
  TRADE = 'TRADE',
  STATE = 'STATE',
  STATES = 'STATES',
  CHECKBOX = 'CHECKBOX',
  ADDRESS = 'ADDRESS',
  EIN = 'EIN',
  DOT = 'DOT',
  ROWS = 'ROWS',
  AMOUNTS = 'AMOUNTS',
}

export type Pairs = Array<[string, unknown]>

export interface ScreenOption {
  value: string
  label: string
  help: string
}

export interface ScreenCondition {
  key: string
  values: unknown[]
  negate: boolean
}

export interface ScreenRow {
  id: string
  label: string
  context: Pairs
  /** This row's own fields, when they differ from the field's rowFields. */
  fields: ScreenField[] | null
}

export interface ScreenField {
  key: string
  label: string
  kind: ScreenFieldKind
  help: string
  placeholder: string
  options: ScreenOption[]
  required: boolean
  showIf: ScreenCondition[]
  exclusive: string[]
  min: number
  max: number
  maxLength: number
  minDate: string
  maxDate: string
  mustCheck: boolean
  lockedState: string
  searchOptions: ScreenOption[]
  amountsFor: string
  totalLabel: string
  totalAmount: number | null
  addLabel: string
  itemLabel: string
  rowFields: ScreenField[]
  rows: ScreenRow[]
  value: unknown
}

export interface AddressValue {
  line1: string
  line2: string
  city: string
  state: string
  zip: string
}

export type AmountsValue = Record<string, number | null>

export interface RowValue {
  id: string
  values: ScreenValues
}

/**
 * An answer while it's being edited: text kinds hold strings ('' when blank), numbers hold number | null,
 * lists hold string[], CHECKBOX a boolean, ADDRESS an AddressValue, AMOUNTS an AmountsValue, ROWS RowValue[].
 */
export type ScreenValue =
  string | number | boolean | string[] | AddressValue | AmountsValue | RowValue[] | null

export type ScreenValues = Record<string, ScreenValue>

export type ScreenErrors = Record<string, string>

/** A field's answers as the server stores them, or the facts a fixed row's conditions read. */
export type Answers = Record<string, unknown>

export const MAX_ROWS = 30
export const EARLIEST_YEAR = 1900
const EARLIEST_ACCEPTED_YEAR = 1800
const SUMMARY_LINES = 6
const SUMMARY_TEXT_LENGTH = 80
const ZIP = /^\d{5}(-\d{4})?$/
const MONTH = /^(\d{4})-(\d{2})$/
const STATE_NAMES = new Map(US_STATES.map(([code, name]) => [code, name]))

export function fromPairs(pairs: unknown): Answers {
  if (!Array.isArray(pairs)) return isRecord(pairs) ? pairs : {}
  return Object.fromEntries(
    pairs.filter((pair): pair is [string, unknown] => Array.isArray(pair) && typeof pair[0] === 'string')
  )
}

export function toPairs(answers: Answers): Pairs {
  return Object.entries(answers)
}

/** The fields a row renders: its own when the server sent them, else the ROWS field's rowFields. */
export function rowFieldsFor(field: ScreenField, rowId: string): ScreenField[] {
  return field.rows.find((row) => row.id === rowId)?.fields ?? field.rowFields
}

export function rowContext(field: ScreenField, rowId: string): Answers {
  return fromPairs(field.rows.find((row) => row.id === rowId)?.context)
}

/** What validate() puts before a row's field keys in their errors: rowsKey.rowId. */
export function rowErrorKey(prefix: string, field: ScreenField, rowId: string): string {
  return `${prefix}${field.key}.${rowId}.`
}

// Initial values.

export function initialValues(fields: ScreenField[]): ScreenValues {
  return Object.fromEntries(fields.map((field) => [field.key, initialValue(field, field.value)]))
}

function initialRowValues(fields: ScreenField[], stored: unknown): ScreenValues {
  const answers = fromPairs(stored)
  return Object.fromEntries(
    fields.map((field) => [
      field.key,
      initialValue(field, field.key in answers ? answers[field.key] : field.value),
    ])
  )
}

export function initialValue(field: ScreenField, raw: unknown): ScreenValue {
  switch (field.kind) {
    case ScreenFieldKind.NUMBER:
    case ScreenFieldKind.MONEY:
    case ScreenFieldKind.YEAR:
      return toNumber(raw)
    case ScreenFieldKind.MULTI:
    case ScreenFieldKind.CARDS:
    case ScreenFieldKind.STATES:
      return Array.isArray(raw) ? raw.map(String) : []
    case ScreenFieldKind.CHECKBOX:
      return raw === true
    case ScreenFieldKind.ADDRESS:
      return initialAddress(raw, field.lockedState)
    case ScreenFieldKind.AMOUNTS:
      return Object.fromEntries(
        Object.entries(fromPairs(raw)).map(([option, amount]) => [option, toNumber(amount)])
      )
    case ScreenFieldKind.ROWS:
      return initialRows(field, raw)
    case ScreenFieldKind.EIN:
      return formatEin(raw == null ? '' : String(raw))
    default:
      return raw == null ? '' : String(raw)
  }
}

function initialAddress(raw: unknown, lockedState: string): AddressValue {
  const stored = isRecord(raw) ? raw : {}
  const part = (name: keyof AddressValue) =>
    typeof stored[name] === 'string' ? (stored[name] as string) : ''
  return {
    line1: part('line1'),
    line2: part('line2'),
    city: part('city'),
    state: part('state') || lockedState,
    zip: part('zip'),
  }
}

function initialRows(field: ScreenField, raw: unknown): RowValue[] {
  const stored = (Array.isArray(raw) ? raw : []).filter(isRecord)
  if (field.rows.length) {
    const byId = new Map(stored.map((row) => [String(row.id), row]))
    return field.rows.map((row) => ({
      id: row.id,
      values: initialRowValues(rowFieldsFor(field, row.id), byId.get(row.id)?.values),
    }))
  }
  return stored.map((row) => ({
    id: String(row.id),
    values: initialRowValues(field.rowFields, row.values),
  }))
}

export function emptyRow(field: ScreenField, id: string): RowValue {
  return { id, values: initialRowValues(field.rowFields, []) }
}

/** The picks after toggling one: an exclusive option ("None of these") clears the rest, and any other clears it. */
export function togglePick(field: ScreenField, picked: string[], option: string, checked: boolean): string[] {
  if (!checked) return picked.filter((value) => value !== option)
  const next = field.exclusive.includes(option)
    ? [option]
    : [...picked.filter((value) => !field.exclusive.includes(value)), option]
  return field.options.map((o) => o.value).filter((value) => next.includes(value))
}

// Visibility.

/** Whether a field shows, given the answers it may depend on: always when it has no conditions, else when any holds. */
export function isVisible(field: ScreenField, scope: Answers): boolean {
  return !field.showIf.length || field.showIf.some((condition) => holds(condition, scope))
}

function holds(condition: ScreenCondition, scope: Answers): boolean {
  const current = scope[condition.key] ?? null
  const items = Array.isArray(current) ? current : [current]
  const matches = items.some((item) => condition.values.some((value) => (value ?? null) === item))
  return matches !== condition.negate
}

interface VisibleField {
  field: ScreenField
  /** The answers before this field, over the row's context: what its conditions and AMOUNTS read. */
  scope: Answers
}

/**
 * The visible fields in order, each with its scope. Like the server, a field's conditions read only the answers
 * to visible fields before it, so a field hidden by its own condition counts as unanswered.
 */
export function visibleFields(
  fields: ScreenField[],
  values: ScreenValues,
  context: Answers = {}
): VisibleField[] {
  const answers: Answers = {}
  const shown: VisibleField[] = []
  for (const field of fields) {
    const scope = { ...context, ...answers }
    if (!isVisible(field, scope)) continue
    shown.push({ field, scope })
    const answer = payloadValue(field, values[field.key] ?? null, scope)
    if (answer !== null) answers[field.key] = answer
  }
  return shown
}

// Payload.

/** What the server's parse() reads: visible fields only, empty optional answers left out. */
export function toPayload(fields: ScreenField[], values: ScreenValues, context: Answers = {}): Answers {
  const payload: Answers = {}
  for (const { field, scope } of visibleFields(fields, values, context)) {
    const answer = payloadValue(field, values[field.key] ?? null, scope)
    if (answer !== null) payload[field.key] = answer
  }
  return payload
}

function payloadValue(field: ScreenField, value: ScreenValue, scope: Answers): unknown {
  switch (field.kind) {
    case ScreenFieldKind.NUMBER:
    case ScreenFieldKind.YEAR:
      return typeof value === 'number' ? value : null
    case ScreenFieldKind.MONEY:
      return typeof value === 'number' ? Math.round(value) : null
    case ScreenFieldKind.MULTI:
    case ScreenFieldKind.CARDS:
    case ScreenFieldKind.STATES:
      return Array.isArray(value) && value.length ? value : null
    case ScreenFieldKind.CHECKBOX:
      return value === true
    case ScreenFieldKind.MONTH:
      return typeof value === 'string' && MONTH.test(value) ? value : null
    case ScreenFieldKind.ADDRESS: {
      const address = value as AddressValue | null
      if (!address || isBlankAddress(address)) return null
      return {
        line1: address.line1.trim(),
        line2: address.line2.trim(),
        city: address.city.trim(),
        state: address.state.trim().toUpperCase(),
        zip: address.zip.trim(),
      }
    }
    case ScreenFieldKind.AMOUNTS: {
      const amounts = (value as AmountsValue | null) ?? {}
      const entries = pickedOptions(field, scope)
        .filter((option) => typeof amounts[option] === 'number')
        .map((option) => [option, Math.round(amounts[option] as number)])
      return entries.length ? Object.fromEntries(entries) : null
    }
    case ScreenFieldKind.ROWS:
      return ((value as RowValue[] | null) ?? []).map((row) => ({
        id: row.id,
        ...toPayload(rowFieldsFor(field, row.id), row.values, rowContext(field, row.id)),
      }))
    default: {
      const text = typeof value === 'string' ? value.trim() : ''
      return text || null
    }
  }
}

/** The options picked in an AMOUNTS field's sibling MULTI, in the same scope. */
export function pickedOptions(field: ScreenField, scope: Answers): string[] {
  const picked = scope[field.amountsFor]
  return Array.isArray(picked) ? picked.map(String) : []
}

/** The amounts entered for the picked options, added up. */
export function amountsTotal(field: ScreenField, value: ScreenValue, scope: Answers): number {
  const amounts = (value as AmountsValue | null) ?? {}
  return pickedOptions(field, scope).reduce((total, option) => total + Math.round(amounts[option] ?? 0), 0)
}

// Validation.

/**
 * The errors by key, mirroring the server's checks. A top-level field's error is under its key; a row's field
 * is under `${rowsKey}.${rowId}.${fieldKey}`.
 */
export function validate(
  fields: ScreenField[],
  values: ScreenValues,
  context: Answers = {},
  prefix = ''
): ScreenErrors {
  const errors: ScreenErrors = {}
  for (const { field, scope } of visibleFields(fields, values, context)) {
    const value = values[field.key] ?? null
    if (field.kind === ScreenFieldKind.ROWS) {
      Object.assign(errors, validateRows(field, (value as RowValue[] | null) ?? [], prefix))
      continue
    }
    const message = fieldError(field, value, scope)
    if (message) errors[prefix + field.key] = message
  }
  return errors
}

function validateRows(field: ScreenField, rows: RowValue[], prefix: string): ScreenErrors {
  if (!field.rows.length && field.required && !rows.length) {
    return { [prefix + field.key]: `Add at least one ${field.itemLabel.toLowerCase() || 'row'}.` }
  }
  const errors: ScreenErrors = {}
  for (const row of rows) {
    Object.assign(
      errors,
      validate(
        rowFieldsFor(field, row.id),
        row.values,
        rowContext(field, row.id),
        rowErrorKey(prefix, field, row.id)
      )
    )
  }
  return errors
}

function fieldError(field: ScreenField, value: ScreenValue, scope: Answers): string | null {
  if (field.kind === ScreenFieldKind.CHECKBOX) {
    return field.mustCheck && value !== true ? 'Tick the box to confirm.' : null
  }
  if (field.kind === ScreenFieldKind.AMOUNTS) return amountsError(field, value, scope)
  if (isEmpty(field, value)) return field.required ? requiredMessage(field) : null

  switch (field.kind) {
    case ScreenFieldKind.TEXT:
    case ScreenFieldKind.LONG_TEXT:
    case ScreenFieldKind.TRADE:
      return (value as string).trim().length > field.maxLength
        ? `Keep this under ${field.maxLength} characters.`
        : null
    case ScreenFieldKind.NUMBER: {
      const number = value as number
      if (!Number.isInteger(number)) return 'Enter a whole number.'
      return number < field.min || number > field.max
        ? `Enter a number from ${field.min.toLocaleString('en-US')} to ${field.max.toLocaleString('en-US')}.`
        : null
    }
    case ScreenFieldKind.MONEY:
      return moneyError(field, value as number)
    case ScreenFieldKind.YEAR: {
      const thisYear = new Date().getFullYear()
      const year = value as number
      return year < EARLIEST_ACCEPTED_YEAR || year > thisYear ? `Pick a year up to ${thisYear}.` : null
    }
    case ScreenFieldKind.DATE:
      return dateError(field, value as string)
    case ScreenFieldKind.MONTH:
      return MONTH.test(value as string) && (value as string) <= format(new Date(), 'yyyy-MM')
        ? null
        : 'Enter a month and year, not in the future.'
    case ScreenFieldKind.CHOICE:
    case ScreenFieldKind.SELECT:
      return field.options.some((option) => option.value === value) ? null : 'Pick one of the answers.'
    case ScreenFieldKind.STATE:
      return STATE_NAMES.has(value as string) ? null : 'Pick a state.'
    case ScreenFieldKind.ADDRESS:
      return addressError(field, value as AddressValue)
    case ScreenFieldKind.EIN:
      return /^\d{9}$/.test(digitsOnly(value as string)) ? null : 'Enter the 9 digits, like 12-3456789.'
    // The server reads a "USDOT" label and refuses anything else with letters, like an MC number.
    case ScreenFieldKind.DOT:
      return null
    default:
      return null
  }
}

function moneyError(field: ScreenField, amount: number): string | null {
  const dollars = Math.round(amount)
  return dollars < field.min || dollars > field.max
    ? `Enter an amount up to $${field.max.toLocaleString('en-US')}.`
    : null
}

function amountsError(field: ScreenField, value: ScreenValue, scope: Answers): string | null {
  const amounts = (value as AmountsValue | null) ?? {}
  for (const option of pickedOptions(field, scope)) {
    const amount = amounts[option]
    if (typeof amount !== 'number') return 'Enter an amount for each type of work.'
    const message = moneyError(field, amount)
    if (message) return message
  }
  return null
}

function dateError(field: ScreenField, value: string): string | null {
  if (!isValid(parse(value, 'yyyy-MM-dd', new Date()))) return 'Enter a date.'
  if ((field.minDate && value < field.minDate) || (field.maxDate && value > field.maxDate)) {
    return dateRangeMessage(field)
  }
  return null
}

export function dateRangeMessage(field: ScreenField): string {
  const first = field.minDate ? format(parseIsoDate(field.minDate), 'MMM d') : ''
  const last = field.maxDate ? format(parseIsoDate(field.maxDate), 'MMM d, yyyy') : ''
  return `Pick a date from ${first} to ${last}.`
}

function addressError(field: ScreenField, address: AddressValue): string | null {
  const state = address.state.trim().toUpperCase()
  if (!address.line1.trim() || !address.city.trim()) return 'Enter the street address and city.'
  if (!STATE_NAMES.has(state)) return 'Pick the state.'
  if (field.lockedState && state !== field.lockedState) {
    return `This address needs to be in ${STATE_NAMES.get(field.lockedState) ?? field.lockedState}.`
  }
  return ZIP.test(address.zip.trim()) ? null : 'Enter a 5-digit ZIP code.'
}

function requiredMessage(field: ScreenField): string {
  switch (field.kind) {
    case ScreenFieldKind.CHOICE:
    case ScreenFieldKind.SELECT:
    case ScreenFieldKind.STATE:
    case ScreenFieldKind.YEAR:
    case ScreenFieldKind.TRADE:
      return 'Pick an answer.'
    case ScreenFieldKind.MULTI:
    case ScreenFieldKind.CARDS:
    case ScreenFieldKind.STATES:
      return 'Pick at least one.'
    default:
      return 'Fill this in.'
  }
}

function isEmpty(field: ScreenField, value: ScreenValue): boolean {
  if (value === null) return true
  if (typeof value === 'string')
    return field.kind === ScreenFieldKind.MONTH ? value === '' || value === '-' : !value.trim()
  if (Array.isArray(value)) return value.length === 0
  if (field.kind === ScreenFieldKind.ADDRESS) return isBlankAddress(value as AddressValue)
  return false
}

function isBlankAddress(address: AddressValue): boolean {
  return Object.values(address).every((part) => !part.trim())
}

// Summary.

/** A short recap of the answers, one "Label: answer" line per answered field, for the chat bubble. */
export function summarize(fields: ScreenField[], values: ScreenValues): string {
  const lines = visibleFields(fields, values).flatMap(({ field, scope }) => {
    const answer = payloadValue(field, values[field.key] ?? null, scope)
    if (answer === null || answer === false || (Array.isArray(answer) && !answer.length)) return []
    const label = field.label.replace(/[?:.\s]+$/, '')
    return [label ? `${label}: ${shown(field, answer)}` : shown(field, answer)]
  })
  return lines.length > SUMMARY_LINES ? [...lines.slice(0, SUMMARY_LINES), '…'].join('\n') : lines.join('\n')
}

function shown(field: ScreenField, answer: unknown): string {
  const labels = new Map(field.options.map((option) => [option.value, option.label]))
  switch (field.kind) {
    case ScreenFieldKind.MONEY:
      return formatDollars(answer as number)
    case ScreenFieldKind.CHECKBOX:
      return answer ? 'Yes' : 'No'
    case ScreenFieldKind.ADDRESS:
      return formatAddress(answer as AddressValue)
    case ScreenFieldKind.STATE:
      return STATE_NAMES.get(answer as string) ?? String(answer)
    case ScreenFieldKind.DATE:
      return format(parseIsoDate(answer as string), 'MMMM d, yyyy')
    case ScreenFieldKind.MONTH:
      return format(parseIsoDate(`${answer as string}-01`), 'MMMM yyyy')
    case ScreenFieldKind.AMOUNTS:
      return Object.entries(answer as Record<string, number>)
        .map(([option, amount]) => `${labels.get(option) ?? option} ${formatDollars(amount)}`)
        .join(', ')
    case ScreenFieldKind.ROWS: {
      const count = (answer as unknown[]).length
      const noun = field.itemLabel.toLowerCase() || 'row'
      return `${count} ${noun}${count === 1 ? '' : 's'}`
    }
    case ScreenFieldKind.TRADE:
      return (
        labels.get(answer as string) ??
        field.searchOptions.find((option) => option.value === answer)?.label ??
        String(answer)
      )
    case ScreenFieldKind.TEXT:
    case ScreenFieldKind.LONG_TEXT: {
      const text = String(answer).replace(/\s+/g, ' ')
      return text.length > SUMMARY_TEXT_LENGTH ? `${text.slice(0, SUMMARY_TEXT_LENGTH - 1)}…` : text
    }
    default:
      return Array.isArray(answer)
        ? answer.map((item) => labels.get(String(item)) ?? String(item)).join(', ')
        : (labels.get(String(answer)) ?? String(answer))
  }
}

// Formatting.

export function formatDollars(amount: number): string {
  return `$${amount.toLocaleString('en-US')}`
}

export function formatAddress(address: AddressValue): string {
  const street = [address.line1, address.line2].filter(Boolean).join(', ')
  return `${street}, ${address.city}, ${address.state} ${address.zip}`
}

export function formatEin(text: string): string {
  const digits = digitsOnly(text).slice(0, 9)
  return digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits
}

export function digitsOnly(text: string): string {
  return text.replace(/\D/g, '')
}

/** A 'YYYY-MM-DD' date at local midnight, so formatting it never shifts a day. */
export function parseIsoDate(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function toNumber(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  if (typeof raw !== 'string' || !raw.trim()) return null
  const number = Number(raw.replace(/[$,\s]/g, ''))
  return Number.isFinite(number) ? number : null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
